import { curriedReplaceAll, isGoogleMapsLink, pipe } from '@frontend/duck-tape';
import { GoogleMapsIcon } from '@frontend/web-icons';
import { concatClasses, safeCss, type ClassName } from '@frontend/web-utils';
import DOMPurify from 'dompurify';
import ReactDOMServer from 'react-dom/server';

// Add a hook to make all links open a new window
DOMPurify.addHook('afterSanitizeAttributes', function (node) {
  // set all elements owning target to target=_blank
  if ('target' in node) {
    node.setAttribute('target', '_blank');
  }
  // set non-HTML/MathML links to xlink:show=new
  if (!node.hasAttribute('target') && (node.hasAttribute('xlink:href') || node.hasAttribute('href'))) {
    node.setAttribute('xlink:show', 'new');
  }
});

type RenderHTMLProps = {
  anchorClassName?: ClassName;
  className?: ClassName;
  html: string;
  onAnchorClick?: (href: string) => void;
};

// Parsing the html using a library like html-react-parser is difficult, because the html is not always well-formed and dealing with nesting
// is difficult. This is the simplest implementation that works
export const RenderHTML = ({ anchorClassName, className, html, onAnchorClick }: RenderHTMLProps) => {
  const finalHtml = pipe(
    html,
    curriedReplaceAll(
      '<a',
      `<a class="${concatClasses(safeCss('underline-offset-2 underline'), anchorClassName)}" target="_blank"`,
    ),
    curriedReplaceAll(
      'ql-align-justify',
      `ql-align-justify ${safeCss('line-clamp-2 break-words text-p-md font-normal')}`,
    ),
    curriedReplaceAll('<br>', '<br/>'),
    curriedReplaceAll('<ul', `<ul class="${safeCss('list-disc pl-xl')}"`),
    curriedReplaceAll('<ol', `<ol class="${safeCss('list-disc pl-xl')}"`),
  );

  const sanitizedHtml = DOMPurify.sanitize(finalHtml);

  // Add a Google Maps icon to all Google Maps links
  const processedHtml = sanitizedHtml.replace(
    /<a\s+(?:[^>]*?\s+)?href="([^"]*)"([^>]*)>(.*?)<\/a>/gi,
    (match, href, attrs, text) => {
      if (isGoogleMapsLink(href)) {
        return `<a href="${href}"${attrs}>${text}<span class="ml-[2px]">${ReactDOMServer.renderToString(<GoogleMapsIcon height={16} width={16} />)}</span></a>`;
      }
      return match;
    },
  );

  return (
    <div
      className={concatClasses('break-words whitespace-pre-wrap', className)}
      dangerouslySetInnerHTML={{ __html: processedHtml }}
      onClick={(event) => {
        if ((event.target as HTMLElement).tagName === 'A') {
          const href = (event.target as HTMLAnchorElement).getAttribute('href');
          if (href && onAnchorClick) {
            onAnchorClick(href);
            event.preventDefault();
          }
        }
      }}
    />
  );
};
